/*
  DefineData.jsx
  ----------------
  Interface pour créer une attestation sur un modèle choisi.
*/

import { useState, useEffect } from 'react';
import {
  Box,
  Text,
  Button,
  Stack,
  Select,
  Accordion,
  Loader,
  Modal,
  ActionIcon,
  Group,
  Tooltip,
  Center,
  MultiSelect,
  Menu,
  rem,
  TextInput,
  FileInput,
  NumberInput,
  Divider,
  Code,
  CopyButton
} from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import { IconApi, IconCheck, IconClock, IconCode, IconCopy, IconDotsVertical, IconExternalLink, IconFileInfo } from '@tabler/icons-react';
import { CodeHighlight } from '@mantine/code-highlight';
import { useUser } from '../tools/Context';
import { useNavigate, useLocation } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { get, post } from '../tools/Request';
import KeyManagementModal from '../tools/KeyManagementModal';

const REACT_APP_BLOCKCHAIN_EXPLORER_ADDRESS = process.env.REACT_APP_BLOCKCHAIN_EXPLORER_ADDRESS;

export default function DefineData() {
  const [contracts, setContracts] = useState([]);
  const [linkedIds, setLinkedIds] = useState([]);
  const [linkAvailable, setLinkAvailable] = useState([]);
  const [selectedContract, setSelectedContract] = useState('');
  const [selectedContractName, setSelectedContractName] = useState('');
  const [contractAddress, setContractAddress] = useState('');
  const [variables, setVariables] = useState([]);
  const [attestations, setAttestations] = useState([]);
  const [opened, setOpened] = useState(['Attestation 1']);
  const [loading, setLoading] = useState(false);
  const [isBatch, setIsBatch] = useState(false);

  const user = useUser();
  const { refreshPage } = useUser();

  const [excelFile, setExcelFile] = useState(null);
  const [generatedJson, setGeneratedJson] = useState('');
  const [requestPreview, setRequestPreview] = useState('');
  const [modalAPI, setModalAPI] = useState(false);
  const [uid, setUid] = useState('');
  const navigate = useNavigate();

  const [contractData, setContractData] = useState(null);
  const [smartContractModal, setSmartContractModal] = useState(false);

  const [isEncrypted, setIsEncrypted] = useState(false);
  const [keys, setKeys] = useState([]);
  const [selectedKey, setSelectedKey] = useState('');

  const [modalMaxHours, setModalMaxHours] = useState(false);
  const [maxHoursValue, setMaxHoursValue] = useState(0);

  const [keyModalOpened, setKeyModalOpened] = useState(false);
  const location = useLocation();

  const handleContractChange = (modelName) => {
    const selected = contracts.find((contract) => contract.name === modelName);
    setSelectedContract(selected ? selected.id : '');
    setSelectedContractName(modelName);

    setMaxHoursValue(selected ? selected.max_hours : 0);
    setIsEncrypted(selected ? selected.encrypted : false);
    setContractAddress(selected ? selected.contract_address : '');
    setUid('');

    if (selected) {
      setVariables(selected.variable.variables);
      if (selected.variable.batch) setIsBatch(true);
      else setIsBatch(false);
    }
    setAttestations([]);
  };

  const redirectProofPage = () => {
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
      navigate(`/proof?uid=${uid}`);
    }, 500);
  };

  useEffect(() => {
    const fetchContracts = async () => {
      refreshPage(false);
      try {
        let response = await get('contract', undefined, localStorage.getItem('userToken'));
        setContracts(response);

        response = await get('key', undefined, localStorage.getItem('userToken'));
        setKeys(response);
        if (response.length > 0) {
          setSelectedKey(response[0].key);
        }
      } catch (error) {
        console.error('Error fetching contract data:', error);
        setContracts([]);
      }

      try {
        const link = await get('link', undefined, localStorage.getItem('userToken'));
        setLinkAvailable(link);
      } catch (error) {
        console.error('Error fetching link data:', error);
        setLinkAvailable([]);
      }
    };
    fetchContracts();
  }, [user, refreshPage]);

  useEffect(() => {
    if (!contracts.length) return;

    const params = new URLSearchParams(location.search);
    const selectedName = params.get('select');
    if (!selectedName) return;

    const exists = contracts.find((c) => c.name === selectedName);
    if (exists) {
      handleContractChange(selectedName);
    }
  }, [contracts, location.search]);

  const handleRemoveAttestation = (index) => {
    const updatedAttestations = attestations.filter((_, idx) => idx !== index);
    setAttestations(updatedAttestations);
    setOpened([`Attestation ${updatedAttestations.length}`]);
  };

  useEffect(() => {
    if (variables.length > 0 && attestations.length === 0) {
      const initialData = {};
      variables.forEach((variable) => {
        initialData[variable.name] = '';
      });
      setAttestations([initialData]);
      setOpened([`Attestation 1`]);
    }
  }, [variables, attestations]);

  const handleInputChange = (attestationIndex, name, value) => {
    setAttestations((prevAttestations) => prevAttestations.map((attestation, index) => (index === attestationIndex ? { ...attestation, [name]: value } : attestation)));
  };

  const handleAddAttestation = () => {
    const newAttestation = {};
    setAttestations([...attestations, newAttestation]);
    setOpened([`Attestation ${attestations.length + 1}`]);
  };

  const handleExcelUpload = (file) => {
    setExcelFile(file);
    const reader = new FileReader();

    reader.onload = (event) => {
      const binaryStr = event.target.result;
      const workbook = XLSX.read(binaryStr, { type: 'binary' });
      const sheetName = workbook.SheetNames[0];
      const sheetData = XLSX.utils.sheet_to_json(workbook.Sheets[sheetName]);

      const variableNames = variables.map((variable) => variable.name);
      const missingColumns = variableNames.filter((name) => !Object.keys(sheetData[0] || {}).includes(name));

      if (missingColumns.length > 0) {
        showNotification({
          title: 'Invalid Excel File',
          message: `The following columns are missing: ${missingColumns.join(', ')}`,
          color: 'red'
        });
        return;
      }

      const excelAttestations = sheetData.map((row) => {
        const newAttestation = {};
        variableNames.forEach((name) => {
          newAttestation[name] = row[name] || '';
        });
        return newAttestation;
      });

      setAttestations(excelAttestations);
      setOpened(['Attestation 1']);
    };

    reader.readAsBinaryString(file);
  };

  useEffect(() => {
    const selectedKeyObject = keys.find((key) => key.key === selectedKey);
    const key_id = isEncrypted ? (selectedKeyObject ? selectedKeyObject.id : null) : null;

    try {
      const requestBody = {
        contract_id: selectedContract,
        variables: attestations.map((att) => {
          const variableObj = {};
          for (let key in att) {
            variableObj[key] = att[key];
          }
          return variableObj;
        }),
        linkedIds: linkedIds,
        key_id: key_id
      };

      const requestDetails = {
        url: 'http://localhost:3002/updateContract',
        method: 'POST',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('userToken')}`,
          'Content-Type': 'application/json'
        },
        body: requestBody
      };

      setRequestPreview(JSON.stringify(requestDetails, null, 2));
    } catch {
      setRequestPreview('Invalid JSON');
    }
  }, [generatedJson, selectedContract, contracts, variables, attestations, selectedContractName, keys, selectedKey, linkedIds, isEncrypted]); // ✅ include isEncrypted

  useEffect(() => {
    const getSmartContract = async () => {
      if (!selectedContract) return;
      try {
        const res = await get('contract/code', { contract_id: selectedContract }, localStorage.getItem('userToken'));
        setContractData(res.content);
      } catch (e) {
        console.log('error', e);
      }
    };

    getSmartContract();
  }, [selectedContract]);

  const handleSubmitFields = async () => {
    try {
      setLoading(true);

      if (!selectedContractName) {
        showNotification({
          title: 'Erreur',
          message: 'Veuillez sélectionner un contrat avant de valider',
          color: 'red',
          autoClose: 3000
        });
        setLoading(false);
        return;
      }

      let key_id = null;
      if (isEncrypted) {
        const selectedKeyObject = keys.find((key) => key.key === selectedKey);
        key_id = selectedKeyObject ? selectedKeyObject.id : null;

        if (!key_id) {
          showNotification({
            title: 'Erreur',
            message: 'Clé sélectionnée invalide. Veuillez vérifier.',
            color: 'red',
            autoClose: 3000
          });
          setLoading(false);
          return;
        }
      }

      const res = await post(
        'attestation',
        {
          contract_id: selectedContract,
          variables: attestations,
          linkedIds: linkedIds,
          key_id: key_id
        },
        localStorage.getItem('userToken')
      );

      setUid(res.id);
      showNotification({
        title: 'Ajout réussi',
        message: 'Les données ont été ajoutées avec succès',
        color: 'teal',
        autoClose: 3000
      });

      setSelectedContract('');
      setGeneratedJson('');
      setSelectedContractName('');
      setLinkedIds([]);
    } catch (error) {
      showNotification({
        title: 'Une erreur est survenue',
        message: error.response ? `Erreur : ${error.response.data.message}` : `Erreur : ${error.message}`,
        color: 'red',
        autoClose: 3000
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCopyUid = () => {
    if (uid) {
      navigator.clipboard
        .writeText(uid)
        .then(() => {
          showNotification({
            title: 'UID copié',
            message: 'ID copié avec succès',
            color: 'green'
          });
        })
        .catch(() => {
          showNotification({
            title: 'Copié erreur',
            message: 'Une erreur est survenue',
            color: 'red'
          });
        });
    }
  };

  return (
    <>
      <Center>
        <Box
          mt="5%"
          backgroundColor="white"
          maw={1000}
          style={{
            flex: 1,
            border: '1px solid #ddd',
            borderRadius: '8px',
            padding: '1rem',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
          }}>
          <Stack>
            <Group justify="space-between" align="center" style={{ width: '100%' }}>
              <Text
                style={{
                  fontSize: '1.25rem',
                  fontWeight: 500
                }}>
                Créer une attestation
              </Text>
              <Menu shadow="md" width={200}>
                <Menu.Target>
                  <ActionIcon size="lg" variant="light">
                    <IconDotsVertical style={{ width: '60%', height: '60%' }} />
                  </ActionIcon>
                </Menu.Target>
                <Menu.Dropdown>
                  <Menu.Item
                    component="a"
                    href={'/docs/docs/how_to_use_datalock/create_attestation'}
                    size="lg"
                    target="_blank"
                    variant="transparent"
                    leftSection={<IconFileInfo style={{ width: rem(14), height: rem(14) }} />}>
                    Voir la documentation
                  </Menu.Item>

                  {selectedContract && (
                    <>
                      <Menu.Item
                        component="a"
                        href={`${REACT_APP_BLOCKCHAIN_EXPLORER_ADDRESS}${contractAddress}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        size="compact-md"
                        leftSection={<IconExternalLink style={{ width: rem(14), height: rem(14) }} />}>
                        Voir sur la blockchain
                      </Menu.Item>
                      <Menu.Item
                        size="lg"
                        variant="transparent"
                        onClick={() => {
                          setMaxHoursValue(contracts.find((c) => c.id === selectedContract)?.max_hours || 0);
                          setModalMaxHours(true);
                        }}
                        leftSection={<IconClock style={{ width: rem(14), height: rem(14) }} />}>
                        Temps maximal
                      </Menu.Item>

                      <Menu.Item onClick={() => setModalAPI(true)} leftSection={<IconApi style={{ width: rem(14), height: rem(14) }} />}>
                        Aperçu de la requête
                      </Menu.Item>
                      <Menu.Item
                        onClick={() => {
                          setSmartContractModal(true);
                        }}
                        leftSection={<IconCode style={{ width: rem(14), height: rem(14) }} />}>
                        Voir le smart contract
                      </Menu.Item>
                    </>
                  )}
                </Menu.Dropdown>
              </Menu>
            </Group>

            {contracts.length > 0 && (
              <>
                <Group>
                  <Select
                    mt={16}
                    w={selectedContract && isBatch ? '78%' : '100%'}
                    placeholder="Choisir un modèle"
                    value={selectedContractName}
                    onChange={handleContractChange}
                    data={contracts.map((contract) => contract.name)}
                  />
                  {selectedContract && isBatch && (
                    <FileInput mt={16} w="20%" placeholder="Télécharger un fichier Excel" accept=".xlsx, .xls" value={excelFile} onChange={handleExcelUpload} />
                  )}
                </Group>

                {selectedContract && (
                  <Box
                    p="md"
                    style={{
                      border: '1px solid #eaeaea',
                      borderRadius: '8px'
                    }}>
                    <Accordion variant="contained" mt="md" value={opened} onChange={(value) => setOpened(value)}>
                      {attestations.map((attestation, index) => (
                        <Accordion.Item value={`Attestation ${index + 1}`} key={index}>
                          <Accordion.Control>
                            <Text style={{ fontWeight: 500 }}>{`Attestation ${isBatch ? index + 1 : ''}`}</Text>
                          </Accordion.Control>
                          <Accordion.Panel>
                            {variables.map((variable, varIndex) => (
                              <Box key={varIndex} mt={16}>
                                <Group>
                                  {variable.type === 'string' && !variable.list && (
                                    <TextInput
                                      w="100%"
                                      label={variable.name}
                                      withAsterisk
                                      description={`${variable.description ? `${variable.description} | ` : ''}Format : chaîne de caractères`}
                                      placeholder={`Entrez la valeur pour ${variable.name}`}
                                      value={attestations[index]?.[variable.name] || ''}
                                      onChange={(e) => handleInputChange(index, variable.name, e.target.value)}
                                    />
                                  )}

                                  {variable.type === 'string' && variable.list && (
                                    <TextInput
                                      w="100%"
                                      label={variable.name}
                                      withAsterisk
                                      description={`${
                                        variable.description ? `${variable.description} | ` : ''
                                      }Format : liste de chaînes entre guillemets, séparées par des virgules. Ex : "aaa","bbb","ccc"`}
                                      placeholder={`Entrez les valeurs pour ${variable.name} (ex : "aaa","bbb","ccc")`}
                                      value={attestations[index]?.[variable.name] || ''}
                                      onChange={(e) => handleInputChange(index, variable.name, e.target.value)}
                                    />
                                  )}

                                  {variable.type === 'int' && !variable.list && (
                                    <NumberInput
                                      w="100%"
                                      label={variable.name}
                                      description={`${variable.description ? `${variable.description} | ` : ''}Format : nombre entier (positif ou négatif)`}
                                      withAsterisk
                                      placeholder={`Entrez la valeur pour ${variable.name}`}
                                      value={attestations[index]?.[variable.name] ?? null}
                                      onChange={(value) => handleInputChange(index, variable.name, value)}
                                    />
                                  )}

                                  {variable.type === 'uint' && !variable.list && (
                                    <NumberInput
                                      w="100%"
                                      label={variable.name}
                                      description={`${variable.description ? `${variable.description} | ` : ''}Format : nombre entier positif (uint)`}
                                      withAsterisk
                                      placeholder={`Entrez la valeur pour ${variable.name}`}
                                      value={attestations[index]?.[variable.name] ?? null}
                                      onChange={(value) => {
                                        if (value != null && value < 0) {
                                          showNotification({
                                            title: 'Valeur invalide',
                                            message: 'Les valeurs uint doivent être positives. La valeur a été remplacée par 0.',
                                            color: 'red'
                                          });
                                          handleInputChange(index, variable.name, 0);
                                        } else {
                                          handleInputChange(index, variable.name, value);
                                        }
                                      }}
                                    />
                                  )}

                                  {variable.type === 'int' && variable.list && (
                                    <TextInput
                                      w="100%"
                                      label={variable.name}
                                      withAsterisk
                                      description={`${
                                        variable.description ? `${variable.description} | ` : ''
                                      }Format : liste de nombres entiers entre guillemets, séparés par des virgules. Ex : "1","-2","3"`}
                                      placeholder={`Entrez les valeurs pour ${variable.name} (ex : "1","-2","3")`}
                                      value={attestations[index]?.[variable.name] || ''}
                                      onChange={(e) => handleInputChange(index, variable.name, e.target.value)}
                                    />
                                  )}

                                  {variable.type === 'uint' && variable.list && (
                                    <TextInput
                                      w="100%"
                                      label={variable.name}
                                      withAsterisk
                                      description={`${
                                        variable.description ? `${variable.description} | ` : ''
                                      }Format : liste de nombres entiers positifs entre guillemets, séparées par des virgules. Ex : "1","2","3"`}
                                      placeholder={`Entrez les valeurs pour ${variable.name} (ex : "1","2","3")`}
                                      value={attestations[index]?.[variable.name] || ''}
                                      onChange={(e) => handleInputChange(index, variable.name, e.target.value)}
                                    />
                                  )}

                                  {variable.type === 'bool' && !variable.list && (
                                    <Select
                                      w="100%"
                                      label={variable.name}
                                      description={`${variable.description ? `${variable.description} | ` : ''}Format : boolean : true ou false`}
                                      withAsterisk
                                      placeholder={`Choisir une valeur pour ${variable.name}`}
                                      data={[
                                        { value: 'true', label: 'True' },
                                        { value: 'false', label: 'False' }
                                      ]}
                                      value={
                                        typeof attestations[index]?.[variable.name] === 'boolean'
                                          ? String(attestations[index][variable.name])
                                          : attestations[index]?.[variable.name] || ''
                                      }
                                      onChange={(value) => handleInputChange(index, variable.name, value === 'true')}
                                    />
                                  )}

                                  {variable.type === 'bool' && variable.list && (
                                    <TextInput
                                      w="100%"
                                      label={variable.name}
                                      withAsterisk
                                      description={`${
                                        variable.description ? `${variable.description} | ` : ''
                                      }Format : liste de booléens entre guillemets, séparées par des virgules. Ex : "true","false","true"`}
                                      placeholder={`Entrez les valeurs pour ${variable.name} (ex : "true","false","true")`}
                                      value={attestations[index]?.[variable.name] || ''}
                                      onChange={(e) => handleInputChange(index, variable.name, e.target.value)}
                                    />
                                  )}
                                </Group>
                              </Box>
                            ))}

                            <Box mt="md" style={{ display: 'flex', justifyContent: 'flex-end' }}>
                              {isBatch && (
                                <Button variant="light" color="red" onClick={() => handleRemoveAttestation(index)}>
                                  Supprimer l'attestation
                                </Button>
                              )}
                            </Box>
                          </Accordion.Panel>
                        </Accordion.Item>
                      ))}
                    </Accordion>

                    {isBatch && (
                      <Center>
                        <Button mt="md" variant="light" onClick={handleAddAttestation}>
                          Ajouter une attestation
                        </Button>
                      </Center>
                    )}
                  </Box>
                )}

                <Stack>
                  <Center>
                    {selectedContract && (
                      <MultiSelect
                        w="70%"
                        label="Lier à d'autres attestations ou documents"
                        placeholder="Sélectionner des identifiants"
                        data={linkAvailable.map((link) => ({
                          value: link.id,
                          label: link.id
                        }))}
                        value={linkedIds}
                        onChange={setLinkedIds}
                        clearable
                        searchable
                      />
                    )}
                  </Center>

                  <Center>
                    {selectedContract && isEncrypted && (
                      <Group w="70%" align="flex-end" justify="space-between">
                        <Select
                          style={{ flex: 1 }}
                          label="Clé de chiffrement"
                          placeholder="Sélectionner une clé"
                          value={selectedKey}
                          onChange={setSelectedKey}
                          data={keys.map((key) => ({ value: key.key, label: key.name }))}
                          required
                          mb="md"
                        />
                        <Button mb={16} variant="light" onClick={() => setKeyModalOpened(true)}>
                          Ajouter une clé
                        </Button>
                      </Group>
                    )}
                  </Center>
                </Stack>

                <Center>
                  <Stack>
                    <Center>
                      {selectedContract && (
                        <Button
                          w={212}
                          color="#0a0082"
                          onClick={handleSubmitFields}
                          disabled={
                            !selectedContract ||
                            attestations.some((attestation) =>
                              Object.values(attestation).some((value) => (typeof value === 'string' && value.trim() === '') || (typeof value === 'number' && value < 0))
                            )
                          }>
                          {loading ? <Loader size="xs" /> : "Créer l'attestation"}
                        </Button>
                      )}
                    </Center>

                    {uid && (
                      <>
                        <Divider />
                        <Group justify="space-between">
                          <Group>
                            <Text size="sm">Identifiant d'insertion : </Text>
                            <Text size="sm">{uid}</Text>
                            <CopyButton value={uid}>
                              {({ copied, copy }) => (
                                <Tooltip label={copied ? 'Copié!' : 'Copier'} withArrow>
                                  <ActionIcon color={copied ? 'teal' : 'blue'} onClick={copy} size="sm">
                                    {copied ? <IconCheck size={16} /> : <IconCopy size={16} />}
                                  </ActionIcon>
                                </Tooltip>
                              )}
                            </CopyButton>
                          </Group>
                        </Group>
                      </>
                    )}

                    {uid && (
                      <Button color="teal" onClick={redirectProofPage} disabled={loading}>
                        {loading ? <Loader size="xs" /> : 'Voir la preuve'}
                      </Button>
                    )}
                  </Stack>
                </Center>
              </>
            )}
          </Stack>
        </Box>
      </Center>

      <Modal opened={modalAPI} onClose={() => setModalAPI(false)} title="Aperçu de la requête" size="lg">
        <CodeHighlight code={requestPreview} language="json" copyLabel="Copier" copiedLabel="Copié!" />
      </Modal>

      <Modal opened={smartContractModal} onClose={() => setSmartContractModal(false)} title="Détail du smart contract" size="lg">
        {contractData ? (
          <Box>
            <Text size="sm" weight={500} mb="xs">
              Adresse du contrat : {contractAddress}
            </Text>
            <Code block>{contractData}</Code>
          </Box>
        ) : (
          <Text>Récupération des données en cours...</Text>
        )}
      </Modal>

      <Modal opened={modalMaxHours} onClose={() => setModalMaxHours(false)} title="Modifier le temps maximal" size="sm">
        <Stack>
          <Text size="sm" c="dimmed">
            Temps maximal en heures avant qu’une attestation ne soit validée sur la blockchain.
          </Text>
          <NumberInput label="Temps maximal (heures)" value={maxHoursValue} onChange={setMaxHoursValue} min={1} max={168} suffix="h" />
          <Button
            color="indigo"
            onClick={async () => {
              try {
                await post(
                  'contract/updateMaxHours',
                  {
                    contract_id: selectedContract,
                    max_hours: maxHoursValue
                  },
                  localStorage.getItem('userToken')
                );
                showNotification({
                  title: 'Succès',
                  message: 'Temps maximal mis à jour.',
                  color: 'teal'
                });
                setModalMaxHours(false);
                refreshPage(true);
              } catch (error) {
                showNotification({
                  title: 'Erreur',
                  message: error.message || 'Une erreur est survenue.',
                  color: 'red'
                });
              }
            }}>
            Sauvegarder
          </Button>
        </Stack>
      </Modal>

      <KeyManagementModal opened={keyModalOpened} defaultTab="add" onClose={() => setKeyModalOpened(false)} />
    </>
  );
}
